import React, { useState } from "react";
import { IonCard, IonCardContent, IonRow, IonCol, IonIcon } from "@ionic/react";
import { chevronDown, chevronUp } from "ionicons/icons";

const defaultLeadingIcon = <span aria-hidden="true">🐴</span>;

/** Kept as numeric exports for skeleton sizing props that need JS numbers. */
export const SUBSECTION_LEADING_ICON_BOX_PX = 48;
export const SUBSECTION_LEADING_ICON_EMOJI_PX = 28;
export const SUBSECTION_LEADING_ICON_BORDER_RADIUS_PX = 10;

function resolveLeadingContent(leadingIcon: React.ReactNode | undefined, leadingEmoji: string | undefined): React.ReactNode {
    if (leadingIcon !== undefined) {
        return leadingIcon;
    }
    if (leadingEmoji !== undefined) {
        return (
            <span aria-hidden="true" className="subsection-card__leading-emoji">
                {leadingEmoji}
            </span>
        );
    }
    return defaultLeadingIcon;
}

/** `collapsible`: header with chevron and expandable body (default). `summaryRow`: one flat bar — icon + title left, children right (no dropdown). */
export type SubSectionCardVariant = "collapsible" | "summaryRow";

interface SubSectionCardProps {
    title: string; // Section title (e.g., "Entry 5 - Chica's Girl")
    titleSecondaryLine?: string;
    titleTertiaryLine?: string;
    stallCount?: number; // Number of stalls for this entry
    children?: React.ReactNode;
    padding?: string; // Custom padding class for content area (e.g., "px-3 py-2"). Defaults to "px-0 py-2"
    /** When false, the leading graphic is omitted (e.g. for non-entry summary blocks). */
    showLeadingGraphic?: boolean;
    /** Content inside the leading square (e.g. IonIcon, or a string emoji like "📅"). If set, overrides leadingEmoji. */
    leadingIcon?: React.ReactNode;
    /** Emoji (or any short string) shown in the leading square. Ignored if leadingIcon is provided. */
    leadingEmoji?: string;
    variant?: SubSectionCardVariant;
    /** `summaryRow` only: icon + title act as a control (e.g. jump to Waivers when incomplete). */
    onSummaryLeadingClick?: () => void;
    /** For `collapsible` only: initial open state (default expanded). Entry Summary passes `false` so sections start collapsed. */
    defaultExpanded?: boolean;
    /** For `collapsible` only: shown to the left of the chevron when the card is collapsed (e.g. subtotal). Hidden when expanded. */
    collapsedHeaderTrailing?: React.ReactNode;
    /** Allow multiline wrapped title text in header instead of single-line truncation. */
    allowWrappedTitle?: boolean;
    /** For `collapsible` only: `true` always shows chevron, `false` hides it, `"expandedOnly"` shows it only when the card is expanded (e.g. so a collapsed status chip can take its place). */
    showChevron?: boolean | "expandedOnly";
    /** For `collapsible` only: compact content shown when the card is collapsed (inside the same card, below header). */
    collapsedContent?: React.ReactNode;
}

const SubSectionCard: React.FC<SubSectionCardProps> = ({
    title,
    titleSecondaryLine,
    titleTertiaryLine,
    stallCount,
    children,
    padding = "px-0 py-2",
    showLeadingGraphic = true,
    leadingIcon,
    leadingEmoji,
    variant = "collapsible",
    onSummaryLeadingClick,
    defaultExpanded = true,
    collapsedHeaderTrailing,
    allowWrappedTitle = false,
    showChevron = true,
    collapsedContent,
}) => {
    const [expandedCard, setExpandedCard] = useState(defaultExpanded);

    const toggleCardExpansion = () => {
        setExpandedCard((prev) => !prev);
    };

    const leadingContent = resolveLeadingContent(leadingIcon, leadingEmoji);
    const combinedTitleForA11y = [title, titleSecondaryLine, titleTertiaryLine].filter(Boolean).join(" ");
    const chevronVisible = showChevron === true || (showChevron === "expandedOnly" && expandedCard);
    /** Entry # / Horse / Rider stack — matches main entry parent cards (`EntrySectionCard`). */
    const isEntryStyleHeader = Boolean(titleSecondaryLine && titleTertiaryLine);

    const titleClassName = [
        "subsection-card__title",
        isEntryStyleHeader ? "" : "font-weight-bold ",
        "text-white ion-text-wrap m-0 p-0",
        allowWrappedTitle ? "subsection-card__title--wrap" : expandedCard ? "subsection-card__title--expanded" : "subsection-card__title--truncated",
    ]
        .filter(Boolean)
        .join(" ");

    if (variant === "summaryRow") {
        return (
            <IonRow>
                <IonCol className="p-0 m-0">
                    <IonCard className="mx-0 my-1 subsection-card">
                        <div className="px-2 py-2 subsection-card__header">
                            {/* Flex row keeps icon + title + trailing chip on one line (Ion grid cols were stacking the chip). */}
                            <div className="subsection-card__header-row">
                                <div
                                    className={`subsection-card__header-leading${onSummaryLeadingClick ? " subsection-card__header-leading--clickable" : ""}`}
                                    onClick={onSummaryLeadingClick}
                                    onKeyDown={(e) => {
                                        if (!onSummaryLeadingClick) return;
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            onSummaryLeadingClick();
                                        }
                                    }}
                                    role={onSummaryLeadingClick ? "button" : undefined}
                                    tabIndex={onSummaryLeadingClick ? 0 : undefined}
                                    aria-label={onSummaryLeadingClick ? `Open ${combinedTitleForA11y}` : undefined}>
                                    {showLeadingGraphic && <div className="subsection-card__leading-icon">{leadingContent}</div>}
                                    <span className="font-weight-bold text-white ion-text-wrap subsection-card__title">
                                        {!titleSecondaryLine ? (
                                            <span className="subsection-card__title-line subsection-card__title-line--secondary">{title}</span>
                                        ) : null}
                                        {titleSecondaryLine ? (
                                            <span className="subsection-card__title-line subsection-card__title-line--horse">{titleSecondaryLine}</span>
                                        ) : null}
                                        {titleTertiaryLine ? (
                                            <span className="subsection-card__title-line subsection-card__title-line--riders">{titleTertiaryLine}</span>
                                        ) : null}
                                    </span>
                                </div>
                                <div className="subsection-card__header-trailing">{children}</div>
                            </div>
                        </div>
                    </IonCard>
                </IonCol>
            </IonRow>
        );
    }

    return (
        <IonRow>
            <IonCol className="p-0 m-0">
                <IonCard className="mx-0 my-1 subsection-card">
                    {/* Same outer padding + flex pattern as `summaryRow` (Waivers bar) so headers align. */}
                    <div
                        className={`px-2 py-2 subsection-card__header ${
                            expandedCard ? "subsection-card__header--expanded" : "subsection-card__header--collapsed"
                        }`}
                        onClick={toggleCardExpansion}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleCardExpansion();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={expandedCard}>
                        <div className="subsection-card__header-row">
                            <div className="subsection-card__header-leading">
                                {showLeadingGraphic ? <div className="subsection-card__leading-icon">{leadingContent}</div> : null}
                                <span
                                    className={titleClassName}
                                    title={!expandedCard && !allowWrappedTitle && combinedTitleForA11y.length > 0 ? combinedTitleForA11y : undefined}>
                                    {title ? (
                                        <span
                                            className={`subsection-card__title-line ${
                                                isEntryStyleHeader
                                                    ? "subsection-card__title-line--primary text-primary"
                                                    : "subsection-card__title-line--secondary"
                                            }`}>
                                            {title}
                                        </span>
                                    ) : null}
                                    {titleSecondaryLine ? (
                                        <span
                                            className={`subsection-card__title-line ${
                                                isEntryStyleHeader
                                                    ? "subsection-card__title-line--horse-entry"
                                                    : "subsection-card__title-line--horse-spaced"
                                            }${allowWrappedTitle ? " subsection-card__title--wrap" : ""}`}>
                                            {titleSecondaryLine}
                                        </span>
                                    ) : null}
                                    {titleTertiaryLine ? (
                                        <span className="subsection-card__title-line subsection-card__title-line--riders-entry">{titleTertiaryLine}</span>
                                    ) : null}
                                </span>
                            </div>
                            <div className="subsection-card__header-trailing">
                                {!expandedCard && collapsedHeaderTrailing ? collapsedHeaderTrailing : null}
                                {chevronVisible ? (
                                    <IonIcon
                                        icon={expandedCard ? chevronDown : chevronUp}
                                        className="text-white subsection-card__chevron"
                                        aria-hidden="true"
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {!expandedCard && collapsedContent ? (
                        <IonCardContent
                            className="px-2 py-1 subsection-card__body subsection-card__body--collapsed"
                            onClick={toggleCardExpansion}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleCardExpansion();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedCard}
                            aria-label={`Expand ${combinedTitleForA11y}`}>
                            {collapsedContent}
                        </IonCardContent>
                    ) : null}

                    {expandedCard && (
                        <IonCardContent
                            className={`${padding} subsection-card__body${
                                stallCount !== undefined ? " subsection-card__body--flat-bottom" : ""
                            }`}>
                            {children}
                        </IonCardContent>
                    )}
                </IonCard>
            </IonCol>
        </IonRow>
    );
};

export default SubSectionCard;
